import type { RecoveryDraft } from "../data/store.js";
import { el } from "../lib/dom.js";
import { emitExchange } from "../lib/exchange.js";
import { listPath, navigate } from "../lib/router.js";
import { actionRow, field, type Popup, type Shell } from "./ui.js";

export function openImport(shell: Shell, trigger?: HTMLElement, recovery?: RecoveryDraft): Popup {
  let saved = false;
  let busy = false;
  let alive = true;
  const initial = recovery ? emitExchange(recovery.content, true) : "";
  const input = el("textarea", { attrs: { id: "import-document", rows: 12, spellcheck: "false",
    "aria-describedby": "import-help import-error", autocapitalize: "off" } });
  input.value = initial;
  const error = el("p", { class: "field-error", attrs: { id: "import-error", hidden: true } });
  const submit = el("button", { class: "import-submit", text: "Import list", attrs: { type: "submit" } });
  const cancel = el("button", { class: "secondary import-cancel", text: "Cancel", attrs: { type: "button" },
    on: { click: () => popup.dismiss() } });
  const say = (message: string) => {
    error.textContent = message; error.hidden = false;
    input.setAttribute("aria-invalid", "true"); shell.announce(message); input.focus();
  };
  input.addEventListener("input", () => { error.hidden = true; error.textContent = ""; input.removeAttribute("aria-invalid"); });
  async function save(): Promise<void> {
    if (busy || !alive) return;
    busy = true; submit.disabled = true; cancel.disabled = true; input.readOnly = true;
    try {
      // Let the pressed state paint before inspecting a bounded clipboard document.
      await new Promise(resolve => setTimeout(resolve, 0));
      const list = await shell.store.importList(input.value, recovery?.id);
      if (!alive) return;
      saved = true;
      busy = false;
      shell.announce("List imported. Saved on this device.");
      navigate(listPath(list.id), { replace: true });
    } catch (reason) {
      if (alive) say(reason instanceof Error ? reason.message : "Could not import this list.");
    } finally {
      busy = false;
      if (alive) { submit.disabled = false; cancel.disabled = false; input.readOnly = false; }
    }
  }
  const form = el("form", { class: "import-form", children: [
    el("p", { class: "popup-note", attrs: { id: "import-help" }, text: "Paste a Full export from Trayflux. This creates a new list. Up to 999 items and 8 MiB." }),
    field("import-document", "Shared list", input), error,
    actionRow([cancel], submit),
  ], on: { submit: event => { event.preventDefault(); void save(); } } });
  const popup = shell.openPopup({ title: recovery ? "Retry import" : "Import list", body: form,
    focus: input, returnFocusTo: trigger, busy: () => busy,
    guard: { dirty: () => !saved && input.value !== initial,
      title: "Discard this paste?", message: recovery ? "Your saved recovery draft will remain. Changes in this form will be lost." : "What you pasted will be lost.",
      keep: "Keep editing", discard: "Discard" }, onClose: () => { alive = false; } });
  return popup;
}

/** Recovery stays in the account's local database until retry is durable or Discard is chosen. */
export function importRecoveries(shell: Shell, fallback: HTMLElement): { node: HTMLElement; dispose(): void } {
  const node = el("section", { class: "import-recoveries", attrs: { "aria-label": "Imports to recover", hidden: true } });
  let shown = "";
  let alive = true;
  const paint = () => {
    const drafts = shell.store.recoveries();
    const next = JSON.stringify(drafts.map(draft => [draft.id, draft.revision]));
    if (next === shown) return;
    shown = next;
    node.hidden = drafts.length === 0;
    node.replaceChildren(...drafts.map(draft => {
      const status = el("p", { class: "muted" });
      const copy = el("button", { class: "secondary recovery-copy", text: "Copy", attrs: { type: "button" } });
      const retry = el("button", { class: "recovery-retry", text: "Retry", attrs: { type: "button" },
        on: { click: () => openImport(shell, retry, draft) } });
      const discard = el("button", { class: "secondary recovery-discard", text: "Discard", attrs: { type: "button" },
        on: { click: () => shell.confirm({ title: "Discard recovered import?", message: "This saved copy will be removed from this device. You cannot undo this.",
          keep: { label: "Cancel", onChoose: () => {} }, act: { label: "Discard", danger: true,
            onChoose: () => { void shell.store.discardRecovery(draft.id).then(() => {
              // The confirmation closes before IndexedDB removes the card.
              // Restore focus only if that removal left it on the page body.
              if (alive && fallback.isConnected && document.activeElement === document.body) fallback.focus();
            }).catch(() => { status.textContent = "Could not discard this draft. Try again."; shell.announce(status.textContent); }); } },
        }, { returnFocusTo: () => discard.isConnected ? discard : fallback }) } });
      const manual = (text: string, trigger = copy) => {
        if (!alive || !copy.isConnected) return;
        const preview = el("textarea", { attrs: { id: "recovery-copy-text", readonly: true, rows: 12 } });
        preview.value = text;
        const close = el("button", { text: "Close", attrs: { type: "button" }, on: { click: () => popup.dismiss() } });
        const popup = shell.openPopup({ title: "Copy recovered list", focus: preview, returnFocusTo: trigger,
          body: el("div", { class: "import-form", children: [field("recovery-copy-text", "Text to copy", preview), close] }) });
        preview.select(); shell.announce("Automatic copying is unavailable. Copy the selected text manually.");
      };
      const select = el("button", { class: "secondary recovery-manual", text: "Copy manually",
        attrs: { type: "button", hidden: true }, on: { click: () => manual(emitExchange(draft.content, true), select) } });
      copy.addEventListener("click", () => {
        const text = emitExchange(draft.content, true);
        if (!navigator.clipboard?.writeText) { manual(text); return; }
        void navigator.clipboard.writeText(text).then(() => { if (!alive || !copy.isConnected) return; status.textContent = "Copied to clipboard."; shell.announce(status.textContent); }, () => {
          if (!alive || !copy.isConnected) return;
          // A delayed failure must not replace a Retry form opened meanwhile.
          // Opening the fallback needs a fresh click on the recovery card.
          select.hidden = false;
          status.textContent = "Automatic copying is unavailable. Choose Copy manually.";
          shell.announce(status.textContent);
        });
      });
      return el("article", { class: "import-recovery", attrs: { "data-recovery": draft.id }, children: [
        el("h2", { text: draft.content.name }), el("p", { text: draft.message }),
        el("p", { class: "muted", text: "The import was refused. This copy is saved on this device." }),
        el("div", { class: "button-row", children: [copy, retry, discard, select] }), status,
      ] });
    }));
  };
  paint();
  const stop = shell.store.onRecoveries(paint);
  return { node, dispose() { alive = false; stop(); } };
}
